import { useEffect } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'next-i18next';
import { RegulatorType } from 'src/helpers/constants';
import { useBreakpoint, useOICLicense } from 'src/helpers/use';

import { useCssClasses } from './styled';

import { useDialog } from '@components/atoms/dialog';
import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import { ApplicantTypeEnum, CPDLicenseTypeEnum } from '@interfaces/course';

export type ConfirmLicenseData = {
  regulatorType: RegulatorType;
  applicantType?: ApplicantTypeEnum;
  licenseType?: CPDLicenseTypeEnum;
  licenseNo: string;
};

export type Props = {
  open?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  licenses: ConfirmLicenseData[];
};

const CpdLicensesConfirmationModal = ({ open, onClose, onConfirm, licenses }: Props) => {
  const dialog = useDialog();
  const theme = useTheme();
  const { isMobile } = useBreakpoint();
  const { t } = useTranslation();
  const classes = useCssClasses();
  const { getLicenseDetailText } = useOICLicense();

  useEffect(() => {
    if (open) {
      dialog.open();
    } else {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    if (!dialog.opened) {
      onClose();
    }
  }, [dialog.opened]);

  const getLicensesText = () => {
    const isSingleLicense = licenses.length === 1;

    return (
      <span>
        {t('license.confirmation.your_chooses_license')}
        {isMobile && '\n'}
        {isSingleLicense ? getSingleLicenseInfoText() : getMultipleLicensesInfoText()}
      </span>
    );
  };

  const getSingleLicenseInfoText = () => {
    const license = licenses[0];

    if (license.regulatorType === RegulatorType.TSI) {
      return `${t('license.confirmation.TSI_license_chooses')} ${license.licenseNo}`;
    }

    const { applicantTypeText, licenseTypeText } = getLicenseDetailText(license.applicantType, license.licenseType);

    return `${applicantTypeText}${licenseTypeText}${t('license.confirmation.single_OIC_choose')} ${license.licenseNo}`;
  };

  const getMultipleLicensesInfoText = () => {
    const licenseInfoArray = licenses.map((license) => {
      const { applicantTypeText, licenseTypeText } = getLicenseDetailText(license.applicantType, license.licenseType);
      return `${applicantTypeText}${licenseTypeText} ${t('license.confirmation.license_no')} ${license.licenseNo}`;
    });
    return licenseInfoArray.join(`${t('license.confirmation.and_another_license')}${isMobile ? '\n' : ''}`);
  };

  return dialog.render(
    <>
      <DialogTitle classes={{ root: classes.root }}>
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
          }}
        >
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            rowGap: 1,
          }}
        >
          <FontAwesomeIcon
            style={{ fontSize: '50px' }}
            color={theme.palette.grey[500]}
            icon="fa-light fa-circle-exclamation"
          />
          <Typography color="primary.main" variant="h3">
            {t('license.confirmation.title')}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Typography className={classes.dialogContentText} variant="body2_deprecated">
            {getLicensesText()}
            {licenses.every((license) => license.regulatorType === RegulatorType.OIC) ? (
              <>
                <br />
                <div className="consent-rule">
                  <div>{t('license.confirmation.consent.rule')}</div>
                  <ul>
                    <li>{t('license.confirmation.consent.course_price_1')}</li>
                    <li>{t('license.confirmation.consent.course_price_2')}</li>
                    <li>{t('license.confirmation.consent.course_price_3')}</li>
                    <li>{t('license.confirmation.consent.course_price_4')}</li>
                  </ul>
                </div>
              </>
            ) : null}
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button variant="outlined" onClick={onClose} disableRipple>
          {t('cancel')}
        </Button>
        <Button variant="contained" color="primary" onClick={onConfirm} disableRipple>
          {t('confirm')}
        </Button>
      </DialogActions>
    </>,
  );
};

export default CpdLicensesConfirmationModal;
